import { Link, NavLink, useNavigate } from "react-router-dom";
import { useState } from "react";
import { Moon, Sun, Menu, X } from "lucide-react";
import Logo from "@/components/Logo";
import FounderBadge from "@/components/FounderBadge";
import { useTheme } from "@/contexts/ThemeContext";
import { useAuth } from "@/contexts/AuthContext";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
} from "@/components/ui/dropdown-menu";

const primaryNav = [
  { to: "/practice", label: "Reading Practice" },
  { to: "/vocabulary", label: "Vocabulary" },
  { to: "/test-lab", label: "Test-Taking Lab" },
  { to: "/sat-prep", label: "SAT / ACT / AP" },
];
const moreNav = [
  { to: "/annotator", label: "Annotation AI" },
  { to: "/fluency", label: "Fluency Studio" },
  { to: "/duel", label: "Vocabulary Duel" },
  { to: "/insights", label: "Parent / Teacher Insights" },
  { to: "/about", label: "About" },
  { to: "/pricing", label: "Pricing" },
  { to: "/contact", label: "Contact" },
];

export default function Header() {
  const { theme, toggle } = useTheme();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  return (
    <header className="sticky top-0 z-50 glass-nav border-b border-border" data-testid="site-header">
      <div className="container-spark flex h-20 items-center justify-between">
        <Logo variant="header" />

        <nav className="hidden lg:flex items-center gap-1">
          {primaryNav.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              data-testid={`nav-${n.to.slice(1)}`}
              className={({ isActive }) =>
                `px-4 py-2 rounded-full text-sm font-medium transition ${
                  isActive ? "bg-secondary text-foreground" : "text-foreground/70 hover:text-foreground hover:bg-secondary/60"
                }`
              }
            >
              {n.label}
            </NavLink>
          ))}
          <DropdownMenu>
            <DropdownMenuTrigger
              data-testid="nav-more-btn"
              className="px-4 py-2 rounded-full text-sm font-medium text-foreground/70 hover:text-foreground hover:bg-secondary/60"
            >
              More
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="rounded-2xl">
              {moreNav.map((n) => (
                <DropdownMenuItem
                  key={n.to}
                  onClick={() => navigate(n.to)}
                  data-testid={`more-${n.to.slice(1)}`}
                  className="rounded-xl"
                >
                  {n.label}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </nav>

        <div className="flex items-center gap-2">
          <button
            onClick={toggle}
            data-testid="theme-toggle-btn"
            aria-label="Toggle theme"
            className="grid h-10 w-10 place-items-center rounded-full border border-border hover:bg-secondary transition"
          >
            {theme === "dark" ? <Sun size={18} /> : <Moon size={18} />}
          </button>

          {user ? (
            <>
              <FounderBadge user={user} size="xs" className="hidden sm:inline-flex" />
              <Link to={user.role === "teacher" ? "/teacher" : user.role === "parent" ? "/parent" : "/dashboard"} className="hidden sm:inline-flex btn-ghost h-10 px-4 py-2 text-sm" data-testid="nav-dashboard">
                Dashboard
              </Link>
              <button
                onClick={() => { logout(); navigate("/"); }}
                className="hidden sm:inline-flex btn-ghost h-10 px-4 py-2 text-sm"
                data-testid="logout-btn"
              >
                Logout
              </button>
            </>
          ) : (
            <>
              <Link to="/login" className="hidden sm:inline-flex btn-ghost h-10 px-4 py-2 text-sm" data-testid="nav-login">
                Login
              </Link>
              <Link to="/register" className="hidden sm:inline-flex btn-primary h-10 px-5 py-2 text-sm" data-testid="nav-start-free">
                Start Free
              </Link>
            </>
          )}

          <button
            onClick={() => setOpen((s) => !s)}
            className="lg:hidden grid h-10 w-10 place-items-center rounded-full border border-border"
            data-testid="mobile-menu-btn"
            aria-label="Menu"
          >
            {open ? <X size={18} /> : <Menu size={18} />}
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden border-t border-border bg-background">
          <div className="container-spark py-4 grid gap-1">
            {[...primaryNav, ...moreNav].map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                onClick={() => setOpen(false)}
                className="px-3 py-2 rounded-xl hover:bg-secondary text-sm font-medium"
                data-testid={`mobile-nav-${n.to.slice(1)}`}
              >
                {n.label}
              </NavLink>
            ))}
            {!user && (
              <div className="flex gap-2 pt-2">
                <Link to="/login" onClick={() => setOpen(false)} className="btn-ghost flex-1 text-sm">Login</Link>
                <Link to="/register" onClick={() => setOpen(false)} className="btn-primary flex-1 text-sm">Start Free</Link>
              </div>
            )}
          </div>
        </div>
      )}
    </header>
  );
}
